import { combineReducers } from '@reduxjs/toolkit'
import { createMigrate, persistReducer } from 'redux-persist'

import { abiApi } from './apis/abi/abiApi'
import { fiatRampApi } from './apis/fiatRamps/fiatRamps'
import { foxyApi } from './apis/foxy/foxyApi'
import { limitOrderApi } from './apis/limit-orders/limitOrderApi'
import { portalsApi } from './apis/portals/portalsApi'
import type { SnapshotState } from './apis/snapshot/snapshot'
import { snapshot, snapshotApi } from './apis/snapshot/snapshot'
import { swapperApi } from './apis/swapper/swapperApi'
import {
  clearActionMigrations,
  clearAddressBookMigrations,
  clearAssetsMigrations,
  clearMarketDataMigrations,
  clearOpportunitiesMigrations,
  clearPortfolioMigrations,
  clearSnapshotMigrations,
  clearSwapsMigrations,
  clearTxHistoryMigrations,
  localWalletMigrations,
} from './migrations'
import { actionSlice } from './slices/actionSlice/actionSlice'
import type { ActionState } from './slices/actionSlice/types'
import type { AddressBookState } from './slices/addressBookSlice/addressBookSlice'
import { addressBookSlice } from './slices/addressBookSlice/addressBookSlice'
import type { AssetsState } from './slices/assetsSlice/assetsSlice'
import { assetApi, assets } from './slices/assetsSlice/assetsSlice'
import { limitOrderInput } from './slices/limitOrderInputSlice/limitOrderInputSlice'
import { limitOrderSlice } from './slices/limitOrderSlice/limitOrderSlice'
import type { LocalWalletState } from './slices/localWalletSlice/localWalletSlice'
import { localWalletSlice } from './slices/localWalletSlice/localWalletSlice'
import { marketApi, marketData } from './slices/marketDataSlice/marketDataSlice'
import type { MarketDataState } from './slices/marketDataSlice/types'
import { opportunitiesApi } from './slices/opportunitiesSlice/opportunitiesApiSlice'
import { opportunities } from './slices/opportunitiesSlice/opportunitiesSlice'
import type { OpportunitiesState } from './slices/opportunitiesSlice/types'
import { portfolio, portfolioApi } from './slices/portfolioSlice/portfolioSlice'
import type { Portfolio } from './slices/portfolioSlice/portfolioSliceCommon'
import type { Preferences } from './slices/preferencesSlice/preferencesSlice'
import { preferences } from './slices/preferencesSlice/preferencesSlice'
import { swapSlice } from './slices/swapSlice/swapSlice'
import type { SwapState } from './slices/swapSlice/types'
import { tradeInput } from './slices/tradeInputSlice/tradeInputSlice'
import type { TxHistory } from './slices/txHistorySlice/txHistorySlice'
import { txHistory, txHistoryApi } from './slices/txHistorySlice/txHistorySlice'

import { getConfig } from '@/config'
import { createLocalStorageAdapter } from '@/lib/profiledStorage'
import { createWorkerStorage } from '@/lib/storage/workerStorage'
import { gridplusSlice } from '@/state/slices/gridplusSlice/gridplusSlice'
import type { GridPlusState } from '@/state/slices/gridplusSlice/types'
import { tradeEarnInput } from '@/state/slices/tradeEarnInputSlice/tradeEarnInputSlice'
import { tradeQuoteSlice } from '@/state/slices/tradeQuoteSlice/tradeQuoteSlice'
import { tradeRampInput } from '@/state/slices/tradeRampInputSlice/tradeRampInputSlice'

export const slices = {
  assets,
  marketData,
  txHistory,
  portfolio,
  preferences,
  opportunities,
  tradeInput,
  limitOrderInput,
  tradeRampInput,
  tradeEarnInput,
  tradeQuote: tradeQuoteSlice,
  limitOrder: limitOrderSlice,
  snapshot,
  localWallet: localWalletSlice,
  gridplus: gridplusSlice,
  addressBook: addressBookSlice,
}

const isProfilingEnabled = getConfig().VITE_FEATURE_PERFORMANCE_PROFILER
const storage = createWorkerStorage(isProfilingEnabled)
const localStorageAdapter = createLocalStorageAdapter(isProfilingEnabled)

const PERSIST_THROTTLE_MS = 1000

const preferencesPersistConfig = {
  key: 'preferences',
  storage: localStorageAdapter,
  blacklist: ['featureFlags'],
  throttle: PERSIST_THROTTLE_MS,
}

const txHistoryPersistConfig = {
  key: 'txHistory',
  storage,
  version: Math.max(...Object.keys(clearTxHistoryMigrations).map(Number)),
  migrate: createMigrate(clearTxHistoryMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const portfolioPersistConfig = {
  key: 'portfolio',
  storage,
  version: Math.max(...Object.keys(clearPortfolioMigrations).map(Number)),
  migrate: createMigrate(clearPortfolioMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const opportunitiesPersistConfig = {
  key: 'opportunities',
  storage,
  version: Math.max(...Object.keys(clearOpportunitiesMigrations).map(Number)),
  migrate: createMigrate(clearOpportunitiesMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const snapshotPersistConfig = {
  key: 'snapshot',
  storage,
  version: Math.max(...Object.keys(clearSnapshotMigrations).map(Number)),
  migrate: createMigrate(clearSnapshotMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const localWalletSlicePersistConfig = {
  key: 'localWallet',
  storage: localStorageAdapter,
  version: Math.max(...Object.keys(localWalletMigrations).map(Number)),
  migrate: createMigrate(localWalletMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const marketDataPersistConfig = {
  key: 'marketData',
  storage,
  version: Math.max(...Object.keys(clearMarketDataMigrations).map(Number)),
  migrate: createMigrate(clearMarketDataMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const assetsPersistConfig = {
  key: 'assets',
  storage,
  version: Math.max(...Object.keys(clearAssetsMigrations).map(Number)),
  migrate: createMigrate(clearAssetsMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const limitOrderApiPersistConfig = {
  key: 'limitOrderApi',
  storage,
  version: 0,
  throttle: PERSIST_THROTTLE_MS,
}

const actionPersistConfig = {
  key: 'action',
  storage,
  version: Math.max(...Object.keys(clearActionMigrations).map(Number)),
  migrate: createMigrate(clearActionMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

const swapPersistConfig = {
  key: 'swap',
  storage,
  version: Math.max(...Object.keys(clearSwapsMigrations).map(Number)),
  throttle: PERSIST_THROTTLE_MS,
}

const gridplusPersistConfig = {
  key: 'gridplus',
  storage: localStorageAdapter,
  version: 0,
  throttle: PERSIST_THROTTLE_MS,
}

const addressBookPersistConfig = {
  key: 'addressBook',
  storage: localStorageAdapter,
  version: Math.max(...Object.keys(clearAddressBookMigrations).map(Number)),
  migrate: createMigrate(clearAddressBookMigrations, { debug: false }),
  throttle: PERSIST_THROTTLE_MS,
}

export const sliceReducers = {
  assets: persistReducer<AssetsState>(assetsPersistConfig, assets.reducer),
  marketData: persistReducer<MarketDataState>(marketDataPersistConfig, marketData.reducer),
  txHistory: persistReducer<TxHistory>(txHistoryPersistConfig, txHistory.reducer),
  portfolio: persistReducer<Portfolio>(portfolioPersistConfig, portfolio.reducer),
  preferences: persistReducer<Preferences>(preferencesPersistConfig, preferences.reducer),
  tradeInput: tradeInput.reducer,
  limitOrderInput: limitOrderInput.reducer,
  tradeRampInput: tradeRampInput.reducer,
  tradeEarnInput: tradeEarnInput.reducer,
  opportunities: persistReducer<OpportunitiesState>(
    opportunitiesPersistConfig,
    opportunities.reducer,
  ),
  tradeQuote: tradeQuoteSlice.reducer,
  limitOrder: limitOrderSlice.reducer,
  snapshot: persistReducer<SnapshotState>(snapshotPersistConfig, snapshot.reducer),
  localWallet: persistReducer<LocalWalletState>(
    localWalletSlicePersistConfig,
    localWalletSlice.reducer,
  ),
  action: persistReducer<ActionState>(actionPersistConfig, actionSlice.reducer),
  swap: persistReducer<SwapState>(swapPersistConfig, swapSlice.reducer),
  gridplus: persistReducer<GridPlusState>(gridplusPersistConfig, gridplusSlice.reducer),
  addressBook: persistReducer<AddressBookState>(addressBookPersistConfig, addressBookSlice.reducer),
}

export const apiSlices = {
  assetApi,
  portfolioApi,
  marketApi,
  txHistoryApi,
  swappersApi: swapperApi,
  foxyApi,
  fiatRampApi,
  snapshotApi,
  portalsApi,
  opportunitiesApi,
  abiApi,
  limitOrderApi,
}

export const apiReducers = {
  [assetApi.reducerPath]: assetApi.reducer,
  [portfolioApi.reducerPath]: portfolioApi.reducer,
  [marketApi.reducerPath]: marketApi.reducer,
  [txHistoryApi.reducerPath]: txHistoryApi.reducer,
  [swapperApi.reducerPath]: swapperApi.reducer,
  [foxyApi.reducerPath]: foxyApi.reducer,
  [fiatRampApi.reducerPath]: fiatRampApi.reducer,
  [snapshotApi.reducerPath]: snapshotApi.reducer,
  [portalsApi.reducerPath]: portalsApi.reducer,
  [opportunitiesApi.reducerPath]: opportunitiesApi.reducer,
  [abiApi.reducerPath]: abiApi.reducer,
  [limitOrderApi.reducerPath]: persistReducer(limitOrderApiPersistConfig, limitOrderApi.reducer),
}

export const reducer = combineReducers(Object.assign({}, sliceReducers, apiReducers))
export type ReduxState = ReturnType<typeof reducer>
